/**
 * Meta browser-Pixel helpers for the /apply funnel.
 *
 * Design goals
 * ────────────
 * 1. Every business event carries a **deterministic, stable `event_id`**
 *    tied to the underlying business transaction (application UUID,
 *    Razorpay order id, Razorpay payment id). This lets a future
 *    first-party Conversions API implementation post the exact same
 *    `event_id` so Meta will deduplicate browser+server events into a
 *    single conversion. No random UUIDs.
 *
 * 2. Each event_id is fired **at most once per browser** — a persistent
 *    `localStorage` set is checked before firing and updated after. So a
 *    refresh, back/forward, retry, or re-render can never double-count
 *    a Purchase (or Lead / InitiateCheckout) for the same transaction.
 *
 * 3. Callers describe the event with a business object (uuid, order id,
 *    payment id) — the helper builds the id and payload. If the Pixel
 *    hasn't loaded yet (adblock, network flake), the call becomes a
 *    silent no-op; nothing else in the /apply flow is affected.
 *
 * Scope of this file
 * ──────────────────
 * Browser Pixel only. This file does NOT talk to graph.facebook.com,
 * does NOT read a CAPI access token, and does NOT contain any server
 * event forwarding. Server-side CAPI is deliberately out of scope for
 * this task — but the `event_id` scheme is designed so that a future
 * CAPI service can send `event_name + event_id` to Meta and get free
 * deduplication against these browser events.
 */

const DEDUPE_KEY = "hfa-meta-pixel-fired-events";

/** Read the persistent "already-fired" set. Any localStorage / JSON
 *  problem returns an empty set — never throws. */
function readFiredSet() {
  try {
    const raw = localStorage.getItem(DEDUPE_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch (_) {
    return new Set();
  }
}

/** Persist the set back. Silent on any storage error (Safari private
 *  mode, quota, disabled storage, etc.). */
function writeFiredSet(set) {
  try {
    // Keep the list bounded so it can never grow unbounded across
    // months of visits. The last 200 event_ids are more than enough
    // to cover any realistic Apply session.
    const arr = Array.from(set).slice(-200);
    localStorage.setItem(DEDUPE_KEY, JSON.stringify(arr));
  } catch (_) { /* noop */ }
}

/** Fire a Pixel event exactly once per `event_id` in this browser.
 *  Returns `true` if the event fired now, `false` if it was already
 *  fired earlier (deduped) or if the Pixel isn't available. */
function fireOnce(eventName, eventId, extraProps) {
  if (!eventId) return false;
  if (typeof window === "undefined") return false;
  const set = readFiredSet();
  if (set.has(eventId)) return false;
  const fbq = window.fbq;
  if (typeof fbq !== "function") {
    // Pixel didn't load (adblock / network). Do NOT record the id as
    // fired — a future page load with the pixel loaded should still
    // get a chance to send it if the caller retries.
    return false;
  }
  try {
    fbq("track", eventName, extraProps || {}, { eventID: eventId });
    set.add(eventId);
    writeFiredSet(set);
    return true;
  } catch (_) {
    return false;
  }
}

/** Application successfully created in Atlas (`POST /finalize` 200).
 *  event_id = `lead_<app_uuid>` — one Lead per application, ever. */
export function trackLead({ appUuid }) {
  if (!appUuid) return false;
  return fireOnce("Lead", `lead_${appUuid}`);
}

/** Razorpay order successfully created (`POST /payment/order` 200).
 *  event_id = `ic_<app_uuid>_<order_id>` — one InitiateCheckout per
 *  distinct Razorpay order created for this application. If the user
 *  abandons and re-opens /apply, a fresh order id yields a fresh event
 *  id and a legitimate second InitiateCheckout. */
export function trackInitiateCheckout({ appUuid, orderId, amountPaise, currency }) {
  if (!appUuid || !orderId) return false;
  const value = typeof amountPaise === "number" ? amountPaise / 100 : undefined;
  return fireOnce(
    "InitiateCheckout",
    `ic_${appUuid}_${orderId}`,
    value !== undefined
      ? { value, currency: currency || "INR" }
      : undefined
  );
}

/** Backend has *verified* the Razorpay signature and flipped the
 *  application to `payment_status=paid` (`POST /payment/verify` 200).
 *  event_id = `purchase_<razorpay_payment_id>` — a Razorpay payment id
 *  is globally unique per successful transaction, so this is the
 *  strongest possible dedupe key. */
export function trackPurchase({ razorpayPaymentId, value, currency }) {
  if (!razorpayPaymentId) return false;
  return fireOnce(
    "Purchase",
    `purchase_${razorpayPaymentId}`,
    {
      value: typeof value === "number" ? value : 1999,
      currency: currency || "INR",
    }
  );
}
